import { Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Constants } from '../../../../shared/interfaces/constants';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import {
	CreateClientRequest,
	CreateClientSubscriptionRequest,
	ManagementClientControllerService,
	ManagementClientResponse,
	ManagementClientSubscriptionControllerService,
	ManagementClientTagControllerService,
	ManagementClientTagRequest,
	ManagementClientTagResponse,
	ManagementResellerControllerService,
	ManagementResellerResponse,
	UpdateClientRequest,
	UpdateClientSubscriptionRequest,
} from 'api-client';
import { SharedService } from '../../../../shared/services/shared.service';
import { take } from 'rxjs/internal/operators/take';
import { MatExpansionModule } from '@angular/material/expansion';
import { UtilsService } from '../../../../shared/services/utils.service';
import { ManagementAccountControllerService } from 'api-auth';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ManagementCategoryClippingControllerService, ManagementCategoryResponse, ManagementTagResponse } from 'api-clipping';
import { ManagementCategoryContractNoticesControllerService } from 'api-contract-notices';
import { ManagementCategoryTranscriptionControllerService } from 'api-transcription';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { environment } from '../../../../../environments/environment';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';

interface ManagementCategoryWithTags extends ManagementCategoryResponse {
	tags?: ManagementTagWithAdditionalInfo[];
	isOpened?: boolean;
}

/** null = thematic tag (no brand kind) — mat-select has first-class support for a
 *  null-valued option as the "clear selection" entry, so no UI-only sentinel is needed here. */
type BrandKindOption = 'COMPANY' | 'COMPETITOR' | null;

interface ManagementClientWithSelection extends ManagementClientResponse {
	checked: boolean;
}

interface ManagementTagWithAdditionalInfo extends ManagementTagResponse {
	priority?: number;
	sentiment?: boolean;
	brandKind?: BrandKindOption;
	aiPrompt?: string;
	checked?: boolean;
	index: number;
	realTime?: boolean;
	realTimeEmails?: string[];
	viber?: boolean;
}
interface InputData {
	mode: 'NEW' | 'EDIT' | 'DEACTIVATE' | 'ACTIVATE' | 'PENDING' | 'DELETE';
	type: 'CLIENT' | 'ACCOUNT' | 'SUBSCRIPTION';
	client: ManagementClientWithSelection;
	item: any;
}
@Component({
	selector: 'app-manage-client',
	standalone: true,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatCardModule,
		MatButtonModule,
		MatCheckboxModule,
		MatTooltipModule,
		MatSelectModule,
		TranslateModule,
		MatDividerModule,
		MatExpansionModule,
		MatChipsModule,
		MatAutocompleteModule,
		MatDatepickerModule,
		MatDialogModule,
		DecimalPipe,
	],
	templateUrl: './manage-client.component.html',
	styleUrl: './manage-client.component.scss',
})
export class ManageClientComponent implements OnDestroy {
	private onDestroy: Subject<boolean> = new Subject();

	manageFormGroup: FormGroup = new FormGroup({});
	availableLanguages = Constants.AVAILABLE_LANGUAGES;
	passwordVisible = false;
	scopes = Constants.SCOPES['CLIENT'];
	subscriptionTypes = Constants.CLIENT_SUBSCRIPTION_TYPES;
	reseller = environment.reseller;
	resellerList: ManagementResellerResponse[] = [];

	separatorKeysCodes: number[] = [ENTER, COMMA];
	@ViewChild('emailToInput', { static: false }) emailTo!: ElementRef<HTMLInputElement>;
	@ViewChild('emailCcInput', { static: false }) emailCc!: ElementRef<HTMLInputElement>;
	@ViewChild('emailBccInput', { static: false }) emailBcc!: ElementRef<HTMLInputElement>;
	selectedEmailsTo: string[] = [];
	selectedEmailsCc: string[] = [];
	selectedEmailsBcc: string[] = [];

	showTagsSection = false;
	tagCategories: ManagementCategoryWithTags[] = [];
	clientTags: ManagementClientTagResponse[] = [];
	readonly TAG_MAX_PRIORITY = 10;
	/** Tag indices with a createClientTag call still in flight. Checking a tag and
	 *  immediately picking Company/Competitor (or any other field) fires a second
	 *  persistClientTag before clientTagId comes back from the first create — since
	 *  the tag doesn't exist yet server-side, that second call would also try to
	 *  create it and get rejected with 409, silently dropping whatever it changed. */
	private creatingTagIndices = new Set<number>();
	/** Tag indices changed since the dialog opened (checked/sentiment/realTime/viber/
	 *  brandKind/priority/realTimeEmails) that have not been sent yet - sent to the
	 *  server only from save()/updateSubscription(), when "Confirm" is clicked.
	 *  Never persisted directly from manageClientTag/manageBrandKind any more - see
	 *  CLIPPING-588: an admin un-checking a tag by mistake used to delete it before
	 *  they could change their mind, and checking Sentiment/Real time/Viber armed a
	 *  live pipeline (AI sentiment, email fan-out, Viber posting) before the admin had
	 *  finished the row, let alone clicked Confirm. */
	private dirtyTagIndices = new Set<number>();
	sendAccountCreationEmail = true;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: InputData,
		private dialogRef: MatDialogRef<ManageClientComponent>,
		private fb: FormBuilder,
		public utils: UtilsService,
		private apiClient: ManagementClientControllerService,
		private apiClientTags: ManagementClientTagControllerService,
		private apiManagementAccount: ManagementAccountControllerService,
		private apiManagementSubscription: ManagementClientSubscriptionControllerService,
		private sharedService: SharedService,
		private translate: TranslateService,
		private apiContractNoticeManagementCategory: ManagementCategoryContractNoticesControllerService,
		private apiClippingManagementCategory: ManagementCategoryClippingControllerService,
		private apiTranscriptionManagementCategory: ManagementCategoryTranscriptionControllerService,
		private apiManagementReseller: ManagementResellerControllerService,
		private dialog: MatDialog
	) {
		this.initializeForm();
	}

	ngOnDestroy(): void {
		this.onDestroy.next(true);
	}

	closeDialog(success: boolean = false) {
		this.dialogRef.close(success);
	}

	initializeForm() {
		switch (this.data.type) {
			case 'CLIENT': {
				this.manageFormGroup = this.fb.group({
					name: [, [Validators.required]],
					description: [, [Validators.maxLength(5000)]],
					taxId: [],
					lang: [this.availableLanguages[0].code.toUpperCase()],
					showTags: [false],
					showSentiment: [false],
					newAnalytics: [false],
					foreignCountry: [false],
					reseller: [],
				});

				this.getResellers();

				if (this.data.client) {
					this.manageFormGroup.patchValue(this.data.client);
				}
				break;
			}
			case 'ACCOUNT': {
				this.manageFormGroup = this.fb.group({
					identifier: [, [Validators.required, Validators.pattern(this.utils.VALIDATION_PATTERNS.get('E_MAIL') || '')]],
					password: [],
					name: [],
					email: [],
					clientId: [],
					role: ['CLIENT'],
					scopes: [],
				});

				if (this.data.item && this.data.mode === 'EDIT') {
					this.manageFormGroup.patchValue(this.data.item);
					this.manageFormGroup.controls['identifier'].disable();
				}
				break;
			}
			case 'SUBSCRIPTION': {
				const currentDate = new Date();
				this.manageFormGroup = this.fb.group({
					type: [Validators.required],
					emailSubject: [],
					emailTo: [, [Validators.pattern(this.utils.VALIDATION_PATTERNS.get('E_MAIL') || '')]],
					emailCc: [, [Validators.pattern(this.utils.VALIDATION_PATTERNS.get('E_MAIL') || '')]],
					emailBcc: [, [Validators.pattern(this.utils.VALIDATION_PATTERNS.get('E_MAIL') || '')]],
					validFrom: [new Date(), Validators.required],
					validTo: [new Date(currentDate.setMonth(currentDate.getMonth() + 1)), [Validators.required]],
					pdfTitle: [],
					/** Max tags the client may follow for this subscription type. `null` = no limit. */
					tagLimit: [null],
					aiSummary: [false],
					detailedClippingReport: [false],
					detailedClippingReportTitle: [],
					viberEnabled: [false],
					/** Left empty on load (see loadExistingData) - only sent when the admin types a
					 *  new value; blank means "keep the stored token". */
					viberChannelToken: [],
					viberShowSentiment: [false],

					clientTagsDetails: this.fb.array([]),
				});

				this.manageFormGroup.controls['type'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(type => {
					if (type) {
						this.showTagsSection = type === 'CLIPPING' || type.includes('CONTRACT_NOTICES') || type === 'TRANSCRIPTIONS';
						this.getClientTags();
					}
				});

				// filter out existing subscription for the client
				const existingSubscriptions = this.data.client.subscriptions ? this.data.client.subscriptions.map((s: any) => s.type) : [];
				this.subscriptionTypes = this.subscriptionTypes.filter(st => existingSubscriptions.indexOf(st) === -1);

				if (this.data.item && this.data.mode === 'EDIT') {
					this.subscriptionTypes.push(this.data.item.type);
					this.manageFormGroup.patchValue(this.data.item);

					if (this.manageFormGroup.controls['emailTo'].value) {
						this.manageEmails('emailTo', 'ADD', this.manageFormGroup.controls['emailTo'].value.join(','));
					}
					if (this.manageFormGroup.controls['emailCc'].value) {
						this.manageEmails('emailCc', 'ADD', this.manageFormGroup.controls['emailCc'].value.join(','));
					}
					if (this.manageFormGroup.controls['emailBcc'].value) {
						this.manageEmails('emailBcc', 'ADD', this.manageFormGroup.controls['emailBcc'].value.join(','));
					}

					this.manageFormGroup.controls['type'].disable();
				} else if (this.data.mode === 'NEW' && this.subscriptionTypes) {
					this.manageFormGroup.controls['type'].setValue(this.subscriptionTypes[0]);
				}

				break;
			}
		}
	}

	checkFormValidity() {
		if (this.data.type === 'CLIENT' || (this.data.type === 'ACCOUNT' && this.data.mode !== 'DEACTIVATE' && this.data.mode !== 'ACTIVATE' && this.data.mode !== 'DELETE')) {
			return this.manageFormGroup.valid;
		} else if (this.data.type === 'SUBSCRIPTION') {
			return this.data.mode === 'DELETE' || (this.selectedEmailsTo.length && this.manageFormGroup.valid);
		}
		return true;
	}

	toggleSendAccountCreationEmail(): void {
		this.sendAccountCreationEmail = !this.sendAccountCreationEmail;
	}

	manageEmails(controlName: 'emailTo' | 'emailCc' | 'emailBcc', action: 'REMOVE' | 'ADD', data: string | MatChipInputEvent) {
		const emailPattern: RegExp = new RegExp(this.utils.VALIDATION_PATTERNS.get('E_MAIL') || '');
		const inputData = typeof data === 'string' ? data : (data as MatChipInputEvent).value;
		let emailList: string[] = [];
		if (inputData) {
			emailList.push.apply(emailList, inputData.replaceAll(' ', '').split(','));
		}
		switch (controlName) {
			case 'emailTo': {
				if (action === 'REMOVE') {
					this.selectedEmailsTo = this.selectedEmailsTo.filter(email => email !== data);
				} else if (action === 'ADD') {
					emailList.forEach(email => {
						if (emailPattern.test(email)) {
							this.selectedEmailsTo.push(email);
						}
					});
					this.manageFormGroup.controls['emailTo'].setValue(null);
					if (this.emailTo) {
						this.emailTo.nativeElement.value = '';
					}
				}
				break;
			}
			case 'emailCc': {
				if (action === 'REMOVE') {
					this.selectedEmailsCc = this.selectedEmailsCc.filter(email => email !== data);
				} else if (action === 'ADD') {
					emailList.forEach(email => {
						if (emailPattern.test(email)) {
							this.selectedEmailsCc.push(email);
						}
					});
					this.manageFormGroup.controls['emailCc'].setValue(null);
					if (this.emailCc) {
						this.emailCc.nativeElement.value = '';
					}
				}
				break;
			}
			case 'emailBcc': {
				if (action === 'REMOVE') {
					this.selectedEmailsBcc = this.selectedEmailsBcc.filter(email => email !== data);
				} else if (action === 'ADD') {
					emailList.forEach(email => {
						if (emailPattern.test(email)) {
							this.selectedEmailsBcc.push(email);
						}
					});
					this.manageFormGroup.controls['emailBcc'].setValue(null);
					if (this.emailBcc) {
						this.emailBcc.nativeElement.value = '';
					}
				}
				break;
			}
		}
	}

	/** Client tags methods */
	manageClientTag(index: number, controlName?: string, eventCheckbox?: MatCheckboxChange, event?: Event | InputEvent) {
		const formGroup = this.clientTagsDetails.controls[index] as FormGroup;

		if (eventCheckbox && controlName) {
			formGroup.controls[controlName].setValue(eventCheckbox.checked);
		}
		if (event && controlName) {
			if (controlName === 'priority') {
				const inputValue = (event.target as HTMLInputElement).value;
				let priority = parseInt(inputValue, 10);
				if (isNaN(priority) || priority < 1 || priority > 10) {
					priority = 10;
				}
				formGroup.controls[controlName].setValue(priority);
			} else {
				formGroup.controls[controlName].setValue((event as InputEvent).data);
			}
		}

		this.dirtyTagIndices.add(index);
	}

	/** Write brandKind explicitly instead of relying on the mat-select's own CVA
	 *  having already written it before this fires — persistClientTag reads
	 *  formGroup.value synchronously, and a stale value there sends the tag's
	 *  previous brandKind back to the server, undoing the pick.
	 *  Picking Company/Competitor auto-checks Sentiment (operator can still uncheck it
	 *  manually); clearing back to thematic (null) leaves Sentiment exactly as it was. */
	manageBrandKind(index: number, event: MatSelectChange) {
		const formGroup = this.clientTagsDetails.controls[index] as FormGroup;
		// eslint-disable-next-line no-console
		console.log('[TAG-DEBUG] manageBrandKind', {
			clickedIndex: index,
			resolvedTagId: formGroup?.get('id')?.value,
			resolvedTagValue: (formGroup as any)?.value,
			clientTagId: formGroup?.get('clientTagId')?.value,
			pickedBrandKind: event.value,
			totalControls: this.clientTagsDetails.length,
		});

		formGroup.controls['brandKind'].setValue(event.value);
		if (event.value === 'COMPANY' || event.value === 'COMPETITOR') {
			formGroup.controls['sentiment'].setValue(true);
		}

		this.dirtyTagIndices.add(index);
	}

	/** Sends every tag row changed since the dialog opened (checked/sentiment/realTime/
	 *  viber/brandKind/priority/realTimeEmails) - called from save()/updateSubscription()
	 *  only, so nothing reaches the server before "Confirm" is clicked. Cancel, Escape and
	 *  a backdrop click all just close the dialog without calling save(), so an untouched
	 *  dirtyTagIndices is discarded along with the rest of the component on destroy. */
	private persistDirtyTags(): void {
		if (this.dirtyTagIndices.size === 0) {
			return;
		}
		const indices = Array.from(this.dirtyTagIndices);
		this.dirtyTagIndices.clear();
		indices.forEach(index => {
			const formGroup = this.clientTagsDetails.controls[index] as FormGroup;
			if (formGroup) {
				this.persistClientTag(formGroup);
			}
		});
	}

	private persistClientTag(formGroup: FormGroup) {
		if (formGroup.controls['checked'].value) {
			if (!formGroup.controls['clientTagId'].value) {
				const index = formGroup.controls['index'].value;
				if (this.creatingTagIndices.has(index)) {
					// A create is already in flight for this tag; whatever changed just now
					// is already sitting in formGroup and will be picked up by the update
					// that follows once that create resolves — don't fire a second create.
					return;
				}
				this.creatingTagIndices.add(index);
				this.createClientTag(formGroup.value);
			} else {
				this.updateClientTag(formGroup.value);
			}
		} else {
			this.deleteClientTag(formGroup.value);
		}
	}

	get clientTagsDetails() {
		return this.manageFormGroup.controls['clientTagsDetails'] as FormArray;
	}

	private addClientTagDetailsControl(tagId: string, data?: ManagementClientTagResponse) {
		this.clientTagsDetails.push(
			this.fb.group({
				id: [tagId],
				tagType: [this.getTagTypeFromSubscriptionType()],
				priority: [data?.priority || 10],
				clientId: [data?.clientId],
				clientTagId: [data?.id],
				// name: [data?.name],
				//sentiment: [data?.sentiment || false],
				aiPrompt: [data?.aiPrompt],
				checked: [data ? true : false],
				index: this.clientTagsDetails.length,
				sentiment: [(data as any)?.sentiment],
				brandKind: [(data as any)?.brandKind || null],
				realTime: [data?.realTime],
				realTimeEmails: [data?.realTimeEmails],
				viber: [data?.viber],
			})
		);
	}

	private updateClientTagsControls(index: number, clientTag?: ManagementClientTagResponse) {
		const formGroup = this.clientTagsDetails.controls[index] as FormGroup;
		formGroup.controls['clientTagId'].setValue(clientTag?.id);
		formGroup.controls['clientId'].setValue(clientTag?.clientId);
	}

	public openEmailDialog(index: number, existingEmails: string[]) {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '600px',
			data: {
				mode: 'USER_ACTION',
				type: 'custom-fields',
				// message: this.translate.instant(''),
				fields: [
					{
						type: 'textarea',
						data: existingEmails,
					},
				],
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				if (result) {
					const emailList = result.data.textarea.replaceAll(' ', '').split(',');
					const formGroup = this.clientTagsDetails.controls[index] as FormGroup;
					formGroup.controls['realTimeEmails'].setValue(emailList);
					this.manageClientTag(index, 'realTimeEmails');
				}
			});
	}
	/** (END)Client tags methods */

	save() {
		switch (this.data.type) {
			case 'CLIENT': {
				if (this.data.mode === 'NEW') {
					this.createClient();
				} else if (this.data.mode === 'EDIT') {
					this.updateClient();
				} else if (this.data.mode === 'DELETE') {
					this.deleteClient();
				}
				break;
			}
			case 'ACCOUNT': {
				if (this.data.mode === 'NEW') {
					this.createClientAccount();
				} else if (this.data.mode === 'EDIT') {
					this.updateClientAccount();
				} else if (this.data.mode === 'ACTIVATE') {
					this.activateClientAccount();
				} else if (this.data.mode === 'DEACTIVATE') {
					this.deactivateClientAccount();
				} else if (this.data.mode === 'DELETE') {
					this.deleteAccount();
				}
				break;
			}
			case 'SUBSCRIPTION': {
				if (this.data.mode === 'NEW') {
					this.createSubscription();
				} else if (this.data.mode === 'EDIT') {
					this.updateSubscription();
				} else if (this.data.mode === 'DELETE') {
					this.deleteSubscription();
				}
				break;
			}
		}
	}

	/** Client methods */
	private getResellers() {
		this.apiManagementReseller
			.getResellersList()
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.resellerList = response;
					if (this.data.client && response.length > 0) {
						this.manageFormGroup.controls['reseller'].setValue(response.find(item => item.id === this.data.client.reseller?.id));
					} else if (response.length > 0) {
						const tenderiMkReseller = response.find(item => item.name === 'Tenderi MK');
						if (tenderiMkReseller) {
							this.manageFormGroup.controls['reseller'].setValue(tenderiMkReseller);
						} else {
							this.manageFormGroup.controls['reseller'].setValue(response[0]);
						}
					}
				},
			});
	}

	private createClient() {
		const request: CreateClientRequest = {
			...this.manageFormGroup.value,
			resellerId: this.manageFormGroup.controls['reseller'].value.id,
		};
		this.apiClient
			.createClient(request)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: err => {
					this.sharedService.showNotification.next({
						message: err.error?.message || this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	private updateClient() {
		const request: UpdateClientRequest = {
			...this.manageFormGroup.value,
			resellerId: this.manageFormGroup.controls['reseller'].value.id,
		};
		this.apiClient
			.updateClient(request, this.data.client.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: err => {
					this.sharedService.showNotification.next({
						message: err.error?.message || this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	private deleteClient() {
		this.apiClient
			.deleteClient(this.data.client.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: err => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
						type: 'ERROR',
					});
				},
			});
	}
	/** (END)Client methods */

	/** Accounts methods */
	private createClientAccount() {
		this.manageFormGroup.controls['clientId'].setValue(this.data.client.id);
		this.manageFormGroup.controls['email'].setValue(this.manageFormGroup.controls['identifier'].value);
		this.apiManagementAccount
			.createAccount({ ...this.manageFormGroup.value, reseller: this.reseller.name })
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	private updateClientAccount() {
		this.apiManagementAccount
			.updateAccount(this.manageFormGroup.value, this.data.item.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	private activateClientAccount() {
		this.apiManagementAccount
			.activateAccount(this.data.item.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTIVATED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	private deactivateClientAccount() {
		this.apiManagementAccount
			.deactivateAccount(this.data.item.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_DEACTIVATED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	private deleteAccount() {
		this.apiManagementAccount
			.deleteAccount(this.data.item.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: err => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
						type: 'ERROR',
					});
				},
			});
	}
	/** (END)Accounts methods */

	/** Subscriptions methods */
	/** Push a "valid to" date to the end of the selected day (23:59:59.999) so the
	 *  subscription stays valid through the whole final day, not just its midnight. */
	/** Tag limit as a number, or `null` when the field is left empty — `null` means no limit. */
	private tagLimitValue(): number | undefined {
		const value = this.manageFormGroup.controls['tagLimit'].value;
		if (value === null || value === undefined || value === '') return undefined;
		const parsed = Number(value);
		return isNaN(parsed) ? undefined : parsed;
	}

	private toEndOfDay(value: any): Date {
		const date = new Date(value);
		date.setHours(23, 59, 59, 999);
		return date;
	}

	private createSubscription() {
		const subscriptionDto: CreateClientSubscriptionRequest = {
			type: this.manageFormGroup.controls['type'].value,
			emailTo: this.selectedEmailsTo,
			emailCc: this.selectedEmailsCc,
			emailBcc: this.selectedEmailsBcc,
			emailSubject: this.manageFormGroup.controls['emailSubject'].value,
			pdfTitle: this.manageFormGroup.controls['pdfTitle'].value,
			tagLimit: this.tagLimitValue(),
			validFrom: this.manageFormGroup.controls['validFrom'].value,
			validTo: this.toEndOfDay(this.manageFormGroup.controls['validTo'].value),
			aiSummary: this.manageFormGroup.controls['aiSummary'].value,
			detailedClippingReport: this.manageFormGroup.controls['detailedClippingReport'].value,
			detailedClippingReportTitle: this.manageFormGroup.controls['detailedClippingReportTitle'].value,
			viberEnabled: this.manageFormGroup.controls['viberEnabled'].value,
			viberChannelToken: this.manageFormGroup.controls['viberChannelToken'].value,
			viberShowSentiment: this.manageFormGroup.controls['viberShowSentiment'].value,
			sendAccountCreationEmail: this.sendAccountCreationEmail,
		};
		this.apiManagementSubscription
			.createClientSubscription(subscriptionDto, this.data.client.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	private updateSubscription() {
		this.persistDirtyTags();

		const subscriptionDto: UpdateClientSubscriptionRequest = {
			type: this.manageFormGroup.controls['type'].value,
			emailTo: this.selectedEmailsTo,
			emailCc: this.selectedEmailsCc,
			emailBcc: this.selectedEmailsBcc,
			emailSubject: this.manageFormGroup.controls['emailSubject'].value,
			pdfTitle: this.manageFormGroup.controls['pdfTitle'].value,
			tagLimit: this.tagLimitValue(),
			validFrom: this.manageFormGroup.controls['validFrom'].value,
			validTo: this.toEndOfDay(this.manageFormGroup.controls['validTo'].value),
			aiSummary: this.manageFormGroup.controls['aiSummary'].value,
			detailedClippingReport: this.manageFormGroup.controls['detailedClippingReport'].value,
			detailedClippingReportTitle: this.manageFormGroup.controls['detailedClippingReportTitle'].value,
			viberEnabled: this.manageFormGroup.controls['viberEnabled'].value,
			// Blank/undefined here means "keep the stored token" (never patched on load - see
			// initializeForm/loadExistingData - so this only carries a value when the admin
			// actually typed a new one).
			viberChannelToken: this.manageFormGroup.controls['viberChannelToken'].value,
			viberShowSentiment: this.manageFormGroup.controls['viberShowSentiment'].value,
			sendAccountCreationEmail: this.sendAccountCreationEmail,
		};

		this.apiManagementSubscription
			.updateClientSubscriptionMgmt(subscriptionDto, this.data.item.clientId, this.data.item.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	private deleteSubscription() {
		this.apiManagementSubscription
			.deleteClientSubscription(this.data.client.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: err => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
						type: 'ERROR',
					});
				},
			});
	}

	accordionAction(action: 'openAll' | 'closeAll' | 'open' | 'close', category?: ManagementCategoryWithTags) {
		switch (action) {
			case 'open':
			case 'close': {
				if (category) {
					category.isOpened = action === 'open';
				}
				break;
			}
		}
	}

	private getTagTypeFromSubscriptionType(): 'CLIPPING' | 'TRANSCRIPTION' | 'CONTRACT_NOTICES' {
		const subscriptionType = this.manageFormGroup.controls['type'].value;
		if (subscriptionType === 'CLIPPING') {
			return 'CLIPPING';
		} else if (subscriptionType === 'TRANSCRIPTIONS') {
			return 'TRANSCRIPTION';
		} else if (subscriptionType.includes('CONTRACT_NOTICES')) {
			return 'CONTRACT_NOTICES';
		}
		return 'CONTRACT_NOTICES';
	}

	private getActiveCategoryControler() {
		const subscriptionType = this.manageFormGroup.controls['type'].value;
		switch (subscriptionType) {
			case 'CLIPPING': {
				return this.apiClippingManagementCategory as any;
			}
			case 'TRANSCRIPTIONS': {
				return this.apiTranscriptionManagementCategory as any;
			}
			default: {
				if (subscriptionType.includes('CONTRACT_NOTICES')) {
					return this.apiContractNoticeManagementCategory as any;
				}
				break;
			}
		}
	}

	private getCategories() {
		// eslint-disable-next-line no-console
		console.log('[TAG-DEBUG] getCategories() called, current clientTagsDetails.length=', this.clientTagsDetails.length);
		this.getActiveCategoryControler()
			.getCategoriesWithTagsForClientMgmt()
			.pipe(take(1))
			.subscribe({
				next: (response: any) => {
					this.tagCategories = response || [];
					(this.tagCategories as ManagementCategoryWithTags[]).forEach(categoryItem => {
						categoryItem.tags?.forEach(tagItem => {
							tagItem.index = this.clientTagsDetails.length;
							const clientTag = this.clientTags.find(item => item.tagId === tagItem.id);
							// eslint-disable-next-line no-console
							console.log('[TAG-DEBUG] building control', {
								tagId: tagItem.id,
								tagValue: (tagItem as any).value,
								assignedIndex: tagItem.index,
								clientTagFound: !!clientTag,
							});
							this.addClientTagDetailsControl(tagItem.id, clientTag);

							if (clientTag) {
								categoryItem.isOpened = true;
							}
						});
					});
					// eslint-disable-next-line no-console
					console.log('[TAG-DEBUG] getCategories() done, final clientTagsDetails.length=', this.clientTagsDetails.length);
				},
				error: () => {
					this.tagCategories = [];
				},
			});
	}
	/** (END)Subscriptions methods */

	private getClientTags() {
		if (this.data.mode === 'EDIT') {
			this.apiClientTags
				.getClientTagsDetailsMgmt(this.data.item.clientId)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.clientTags = response;
						this.getCategories();
					},
				});
		} else {
			this.getCategories();
		}
	}

	private createClientTag(tag: ManagementTagWithAdditionalInfo) {
		const tagDto: ManagementClientTagRequest = {
			tagId: tag.id,
			tagType: this.getTagTypeFromSubscriptionType(),
			priority: tag.priority || 10,
			sentiment: tag.sentiment || false,
			brandKind: tag.brandKind || undefined,
			aiPrompt: tag.aiPrompt,
			realTime: tag.realTime || false,
			realTimeEmails: tag.realTimeEmails || [],
			viber: tag.viber || false,
		};
		this.apiClientTags
			.createClientTagMgmt(tagDto, this.data.item.clientId)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
						type: 'SUCCESS',
					});
					this.updateClientTagsControls(tag.index, response);
					this.clientTags.push(response);
					this.creatingTagIndices.delete(tag.index);

					// Persist whatever changed on this tag while the create was in flight
					// (e.g. Company/Competitor picked, or the tag unchecked again, right
					// after checking it) — that change was held back by persistClientTag's
					// in-flight guard and never sent on its own. Route back through
					// persistClientTag rather than calling updateClientTag directly so an
					// unchecked-during-create tag correctly gets deleted, not left behind.
					const formGroup = this.clientTagsDetails.controls[tag.index] as FormGroup;
					this.persistClientTag(formGroup);
				},
				error: () => {
					this.creatingTagIndices.delete(tag.index);
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	private updateClientTag(tag: ManagementTagWithAdditionalInfo) {
		const tagDto: ManagementClientTagRequest = {
			priority: tag.priority || 10,
			tagType: this.getTagTypeFromSubscriptionType(),
			tagId: tag.id,
			sentiment: tag.sentiment || false,
			brandKind: tag.brandKind || undefined,
			aiPrompt: tag.aiPrompt,
			realTime: tag.realTime || false,
			realTimeEmails: tag.realTimeEmails || [],
			viber: tag.viber || false,
		};
		this.apiClientTags
			.updateClientTagMgmt(tagDto, this.data.item.clientId, tag.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					// this.sharedService.showNotification.next({
					// 	message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
					// 	type: 'SUCCESS',
					// });
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	private deleteClientTag(tag: ManagementTagWithAdditionalInfo) {
		this.apiClientTags
			.deleteClientTagMgmt(this.data.item.clientId, tag.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
						type: 'SUCCESS',
					});
					this.clientTags = this.clientTags.filter(tagItem => tagItem.id !== tag.id);
					this.updateClientTagsControls(tag.index);
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}
}
